const CAMPAIGN_ATTRIBUTION_STORAGE_KEY = "soc_campaign_attribution";
const TRACE_IDENTIFIER_QUERY_PARAM = "trace_identifier";
const LEGACY_CAMPAIGN_SESSION_QUERY_PARAM = "sessionId";
const MIXPANEL_CAMPAIGN_SESSION_PROPERTY = "trace_identifier";
const MIXPANEL_TRACE_ID_PROPERTY = "trace_id";
const ATTRIBUTION_QUERY_PARAMS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content"
]);
const MAX_ATTRIBUTION_VALUE_LENGTH = 100;
const MAX_TRACE_IDENTIFIER_LENGTH = 128;
const SAFE_ATTRIBUTION_VALUE_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9._~ -]{0,99})$/;
const SAFE_TRACE_IDENTIFIER_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9._:-]{0,127})$/;

const canUseWindow = () => typeof window !== "undefined";

const getSessionStorage = () => {
  if (!canUseWindow()) return null;

  try {
    return window.sessionStorage || null;
  } catch (_error) {
    return null;
  }
};

const normalizeTraceIdentifier = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_TRACE_IDENTIFIER_LENGTH ||
    !SAFE_TRACE_IDENTIFIER_PATTERN.test(normalized)
  ) {
    return "";
  }
  return normalized;
};

const normalizeAttributionValue = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_ATTRIBUTION_VALUE_LENGTH ||
    !SAFE_ATTRIBUTION_VALUE_PATTERN.test(normalized)
  ) {
    return "";
  }
  return normalized;
};

const readTraceIdentifierFromUrl = (url = canUseWindow() ? window.location.href : "") => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url, canUseWindow() ? window.location.origin : "https://portal.local");
    const traceValues = parsedUrl.searchParams.getAll(TRACE_IDENTIFIER_QUERY_PARAM);
    const legacyTraceValues = parsedUrl.searchParams.getAll(
      LEGACY_CAMPAIGN_SESSION_QUERY_PARAM
    );
    if (traceValues.length > 1 || legacyTraceValues.length > 1) return "";
    if (traceValues.length === 1 && legacyTraceValues.length === 1) return "";

    return normalizeTraceIdentifier(traceValues[0] || legacyTraceValues[0]);
  } catch (_error) {
    return "";
  }
};

const readSessionIdFromUrl = readTraceIdentifierFromUrl;

const readAttributionParamsFromUrl = (
  url = canUseWindow() ? window.location.href : ""
) => {
  if (!url) return {};

  try {
    const parsedUrl = new URL(
      url,
      canUseWindow() ? window.location.origin : "https://portal.local"
    );
    const attribution = {};

    for (const key of ATTRIBUTION_QUERY_PARAMS) {
      const values = parsedUrl.searchParams.getAll(key);
      if (values.length !== 1) continue;

      const value = normalizeAttributionValue(values[0]);
      if (value) attribution[key] = value;
    }

    const traceIdentifier = readTraceIdentifierFromUrl(parsedUrl.toString());
    if (traceIdentifier) {
      attribution[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] = traceIdentifier;
      attribution[MIXPANEL_TRACE_ID_PROPERTY] = traceIdentifier;
    }

    return attribution;
  } catch (_error) {
    return {};
  }
};

const getStoredCampaignAttribution = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const rawValue = storage.getItem(CAMPAIGN_ATTRIBUTION_STORAGE_KEY);
    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue);
    const traceIdentifier = normalizeTraceIdentifier(
      parsedValue?.[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] ||
        parsedValue?.[MIXPANEL_TRACE_ID_PROPERTY]
    );
    const attribution = {};

    if (traceIdentifier) {
      attribution[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] = traceIdentifier;
      attribution[MIXPANEL_TRACE_ID_PROPERTY] = traceIdentifier;
    }
    for (const key of ATTRIBUTION_QUERY_PARAMS) {
      const value = normalizeAttributionValue(parsedValue?.[key]);
      if (value) attribution[key] = value;
    }

    return Object.keys(attribution).length > 0 ? attribution : null;
  } catch (_error) {
    return null;
  }
};

const storeCampaignAttribution = (attributionValue, now = () => new Date()) => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const inputAttribution =
    typeof attributionValue === "string"
      ? {
          [MIXPANEL_CAMPAIGN_SESSION_PROPERTY]: attributionValue,
          [MIXPANEL_TRACE_ID_PROPERTY]: attributionValue
        }
      : attributionValue;
  const traceIdentifier = normalizeTraceIdentifier(
    inputAttribution?.[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] ||
      inputAttribution?.[MIXPANEL_TRACE_ID_PROPERTY]
  );
  const attribution = {};

  if (traceIdentifier) {
    attribution[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] = traceIdentifier;
    attribution[MIXPANEL_TRACE_ID_PROPERTY] = traceIdentifier;
  }
  for (const key of ATTRIBUTION_QUERY_PARAMS) {
    const value = normalizeAttributionValue(inputAttribution?.[key]);
    if (value) attribution[key] = value;
  }

  if (Object.keys(attribution).length === 0) return null;

  try {
    storage.setItem(
      CAMPAIGN_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify({
        ...attribution,
        captured_at: now().toISOString()
      })
    );
  } catch (_error) {
    return null;
  }

  return attribution;
};

const captureCampaignAttribution = ({
  url = canUseWindow() ? window.location.href : "",
  now,
} = {}) => {
  const incomingAttribution = readAttributionParamsFromUrl(url);
  const storedAttribution = getStoredCampaignAttribution();
  if (Object.keys(incomingAttribution).length === 0) return storedAttribution;

  const incomingTrace =
    incomingAttribution[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] || "";
  const storedTrace =
    storedAttribution?.[MIXPANEL_CAMPAIGN_SESSION_PROPERTY] || "";
  const shouldResetStored = Boolean(
    incomingTrace && storedTrace && incomingTrace !== storedTrace
  );

  return storeCampaignAttribution(
    {
      ...(shouldResetStored ? {} : storedAttribution),
      ...incomingAttribution
    },
    now
  );
};

const clearCampaignAttribution = () => {
  const storage = getSessionStorage();
  try {
    storage?.removeItem(CAMPAIGN_ATTRIBUTION_STORAGE_KEY);
  } catch (_error) {
    // Storage can be unavailable in private browsing or hardened contexts.
  }
};

export {
  ATTRIBUTION_QUERY_PARAMS,
  CAMPAIGN_ATTRIBUTION_STORAGE_KEY,
  LEGACY_CAMPAIGN_SESSION_QUERY_PARAM,
  MIXPANEL_CAMPAIGN_SESSION_PROPERTY,
  MIXPANEL_TRACE_ID_PROPERTY,
  TRACE_IDENTIFIER_QUERY_PARAM,
  captureCampaignAttribution,
  clearCampaignAttribution,
  getStoredCampaignAttribution,
  readAttributionParamsFromUrl,
  readSessionIdFromUrl,
  readTraceIdentifierFromUrl,
  storeCampaignAttribution,
};
